import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import ErrorBoundary from '@sbt-web/error-boundary';
import type { AdItem, CorrelatedTrackingInfo } from '@sbt-web/network/types';
import { RelatedAds as RelatedAdsComponent, RelatedAdsProps } from '@/client/components/Detail/RelatedAds';
import { HADES_PATH } from '@/shared/constants';
import { isPro } from '@/shared/models/Advertiser';
import { PULSE_SCHEMA_VERSION } from '@/tools/tracking/constants';
import { getOrCreatePulse } from '@/tools/tracking/utils';
import type { OptionalSectionProps } from './section';
import { useOptionalSection } from './utils';
const getItemId = function (urn: string) {
  return `sdrn:subito:classified:${urn}`;
};
const fallbackId = 'sdrn:schibsted:environment:undefined';
export const getTrackingId = async (): Promise<string> => {
  const pulse = getOrCreatePulse();
  if (pulse) {
    const id = await pulse.getEnvironmentId();
    //To avoid status 400 from Adevinta web service, instead of
    //empty string send undefined
    if (id !== '') {
      return `sdrn:schibsted:environment:${id}`;
    } else {
      return fallbackId;
    }
  } else {
    return fallbackId;
  }
};
const commonTrackingConfig = function (adItem: AdItem, trackingInfo?: CorrelatedTrackingInfo) {
  return {
    recommendation: {
      '@id': trackingInfo?.transactionID,
      '@type': 'RecommendationMetadata',
      recommendationType: 'related-items',
      listName: trackingInfo?.listName,
      source: 'ad-view-detail',
      queryParams: {
        itemId: getItemId(adItem.urn)
      }
    },
    schema: PULSE_SCHEMA_VERSION
  };
};
export const trackImpression = function (adItem: AdItem, adItems: AdItem[], type: string, name: string, trackingInfo?: CorrelatedTrackingInfo) {
  const data = {
    type,
    name,
    object: {
      '@type': 'RecommendationList',
      '@id': getItemId(adItem.urn),
      numItems: adItems.length,
      items: adItems.map((itm, index) => ({
        '@id': getItemId(itm.urn),
        '@type': 'RecommendationItem',
        rank: index + 1
      }))
    },
    ...commonTrackingConfig(adItem, trackingInfo)
  };
  getOrCreatePulse()?.queueEvent(data);
  return data;
};
export const trackClick = function (adItem: AdItem, adClickedUrn: string, index: number, trackingInfo?: CorrelatedTrackingInfo) {
  const data = {
    type: 'Click',
    name: 'Recommendation widget clicked',
    ...commonTrackingConfig(adItem, trackingInfo),
    object: {
      '@id': getItemId(adClickedUrn),
      '@type': 'RecommendationItem',
      rank: index + 1
    }
  };
  getOrCreatePulse()?.queueEvent(data);
  return data;
};
export const trackAutoClick = function (adItem: AdItem, adClickedUrn: string, index: number, trackingInfo: CorrelatedTrackingInfo) {
  const data = {
    type: 'Click',
    name: 'Recommendation widget clicked',
    ...commonTrackingConfig(adItem, trackingInfo),
    object: {
      '@id': getItemId(adClickedUrn),
      '@type': 'RecommendationItem',
      rank: index + 1,
      name: 'Click on Subito Recommender'
    }
  };
  getOrCreatePulse()?.queueEvent(data);
  return data;
};
type Props = OptionalSectionProps & {
  item: AdItem;
  className?: string;
};
export const RelatedAds = t0 => {
  const $ = _c(20);
  const {
    className,
    item,
    onActualRender
  } = t0;
  const [render, setRender] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [relatedAds, setRelatedAds] = useState(t1);
  useOptionalSection(render && relatedAds.length > 0, onActualRender);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = getTrackingId();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const trackingIdPromise = t2;
  let t3;
  if ($[2] !== item) {
    t3 = (items, trackingInfo) => {
      setIsEmpty(items.length === 0);
      setRelatedAds(items);
      trackImpression(item, items, "View", "Recommendation widget impression", trackingInfo);
    };
    $[2] = item;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const onLoaded = t3;
  let t4;
  if ($[4] !== item) {
    t4 = (items_0, trackingInfo_0) => trackImpression(item, items_0, "Engagement", "Recommendation widget viewable impression", trackingInfo_0);
    $[4] = item;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const onVisible = t4;
  let t5;
  if ($[6] !== item) {
    t5 = (itemClicked, index, trackingInfo_1) => trackClick(item, itemClicked.urn, index, trackingInfo_1);
    $[6] = item;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const onClick = t5;
  let t6;
  if ($[8] !== item.advertiser.type) {
    t6 = () => {
      let destroyed = false;
      if (isPro(item.advertiser.type)) {
        setRender(false);
      } else {
        (async () => {
          const trackingId = await trackingIdPromise;
          if (!destroyed && trackingId) {
            setRender(true);
          }
        })();
      }
      return () => {
        destroyed = true;
      };
    };
    $[8] = item.advertiser.type;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== item) {
    t7 = [item, trackingIdPromise];
    $[10] = item;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== className || $[13] !== isEmpty || $[14] !== item.urn || $[15] !== onClick || $[16] !== onLoaded || $[17] !== onVisible || $[18] !== render) {
    t8 = render && !isEmpty ? <section className={className}><RelatedAdsComponent onClick={onClick} onLoaded={onLoaded as RelatedAdsProps['onLoaded']} onVisible={onVisible as RelatedAdsProps['onVisible']} urn={item.urn} userId={trackingIdPromise} apiBase={HADES_PATH} useWorker={true} /></section> : null;
    $[12] = className;
    $[13] = isEmpty;
    $[14] = item.urn;
    $[15] = onClick;
    $[16] = onLoaded;
    $[17] = onVisible;
    $[18] = render;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};
RelatedAds.displayName = 'RelatedAds';
const WrappedRelatedAds: FC<Props> = t0 => {
  const $ = _c(4);
  const {
    item,
    onActualRender,
    className
  } = t0;
  let t1;
  if ($[0] !== className || $[1] !== item || $[2] !== onActualRender) {
    t1 = <ErrorBoundary><RelatedAds item={item} onActualRender={onActualRender} className={className} /></ErrorBoundary>;
    $[0] = className;
    $[1] = item;
    $[2] = onActualRender;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export default WrappedRelatedAds;